import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Modal,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { md5 } from 'js-md5';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';

import { COLORS } from '@constants/colors';
import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import { usePreventGameKeydownListening } from '@hooks';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService, {
  IBrandingHotspotApi,
  IUpdateBrandingHotspotData,
} from '@services/BrandingsService';
import FilesService from '@services/FilesService';

import { useEditorMode } from '../EditorModeContext';
import { startHotspotPlaceInUnreal } from '../hotspotsUnreal';
import { useInspectorStyles } from './styles';

interface HotspotInspectorProps {
  hotspotId: string;
}

type AnyHotspot = Record<string, unknown> & {
  brandingHotspotId?: string;
  name?: string;
  type?: string;
  isActive?: boolean;
  isInteractive?: boolean;
  position?: number[];
  data?: { url?: string; webBrowserUrl?: string; location?: string };
};

const VIDEO_URL_RE = /\.(mp4|webm|mov|m4v|ogg)$/;

const SCALE_MIN = 0.01;
const SCALE_MAX = 100;
const SCALE_DEFAULT = 1;

type ScaleMode = 'UNIFORM' | 'PER_AXIS';

/**
 * Reports whether `url` looks like a supported video by its extension.
 *
 * @param url - URL to inspect.
 */
const isVideoUrl = (url: string): boolean =>
  VIDEO_URL_RE.test(url.toLowerCase().split('?')[0]);

/**
 * Reports whether a numeric value is a finite scale factor within the allowed bounds.
 *
 * @param value - Number to validate.
 */
const isValidScale = (value: number): boolean =>
  Number.isFinite(value) && value >= SCALE_MIN && value <= SCALE_MAX;

/**
 * Parses a raw string from a scale input into a valid scale factor.
 *
 * @param raw - Raw text from the input.
 * @returns The parsed number, or `null` when the input is empty or invalid.
 */
const parseScaleInput = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return null;
  }
  const parsed = Number(trimmed);
  if (!isValidScale(parsed)) {
    return null;
  }
  return parsed;
};

/**
 * Formats a numeric scale factor for display in an input field.
 *
 * @param value - Numeric scale factor.
 */
const formatScaleInput = (value: number): string => {
  if (!Number.isFinite(value)) {
    return String(SCALE_DEFAULT);
  }
  return String(value);
};

/**
 * Derives the initial scale-editor state from a hotspot's stored `scale` value.
 * A uniform vec3 tuple (`x === y === z`) collapses back to uniform mode; a
 * non-uniform tuple opens in per-axis mode. Absent / scalar values map to uniform.
 *
 * @param scale - Stored scale on the hotspot: absent, a scalar, or a `[x, y, z]` tuple.
 * @returns The mode plus per-field string values to seed the inputs.
 */
const deriveScaleState = (
  scale: number | number[] | undefined,
): {
  mode: ScaleMode;
  uniform: string;
  x: string;
  y: string;
  z: string;
} => {
  if (typeof scale === 'number' && Number.isFinite(scale)) {
    const asString = formatScaleInput(scale);
    return {
      mode: 'UNIFORM',
      uniform: asString,
      x: asString,
      y: asString,
      z: asString,
    };
  }
  if (
    Array.isArray(scale) &&
    scale.length === 3 &&
    scale.every((component) => Number.isFinite(component))
  ) {
    const [x, y, z] = scale as [number, number, number];
    if (x === y && y === z) {
      const asString = formatScaleInput(x);
      return {
        mode: 'UNIFORM',
        uniform: asString,
        x: asString,
        y: asString,
        z: asString,
      };
    }
    return {
      mode: 'PER_AXIS',
      // When collapsing back to Uniform later, the X component is used as
      // the seed — mirror that so the shown uniform value stays consistent.
      uniform: formatScaleInput(x),
      x: formatScaleInput(x),
      y: formatScaleInput(y),
      z: formatScaleInput(z),
    };
  }
  const asString = String(SCALE_DEFAULT);
  return {
    mode: 'UNIFORM',
    uniform: asString,
    x: asString,
    y: asString,
    z: asString,
  };
};

const ALLOWED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg'];
const ALLOWED_VIDEO_MIME_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const IMAGE_FILENAME_RE = /\.(png|jpe?g)$/;
const VIDEO_FILENAME_RE = /\.(mp4|webm|mov|m4v)$/;
const MODEL_FILENAME_RE = /\.glb$/;
const MODEL_MIME_TYPE = 'model/gltf-binary';

const getFilename = (url: string): string => {
  if (!url) {
    return '';
  }
  try {
    const parsed = new URL(url, window.location.href);
    return decodeURIComponent(parsed.pathname.split('/').pop() || '');
  } catch {
    return decodeURIComponent(url.split('/').pop() || '');
  }
};

const activeRowStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  borderRadius: '10px',
  backgroundColor: 'rgba(255, 255, 255, 0.045)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
};

const toggleHintStyles = {
  fontSize: '11px',
  lineHeight: 1.45,
  color: 'rgba(255, 255, 255, 0.55)',
  margin: '6px 2px 0',
};

const saveButtonStyles = {
  textTransform: 'none',
  backgroundColor: COLORS.MYTAVERSE_BLUE,
  color: COLORS.WHITE,
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 600,
  padding: '9px 14px',
  boxShadow: 'none',
  gap: '6px',
  '&:hover': { backgroundColor: '#3A46C1', boxShadow: 'none' },
};

const uploadModelBannerStyles = {
  display: 'flex',
  gap: '9px',
  padding: '11px 12px',
  borderRadius: '11px',
  backgroundColor: 'rgba(245, 166, 35, 0.14)',
  color: '#f6d49a',
  marginBottom: '8px',
};

const deleteButtonStyles = {
  textTransform: 'none',
  color: '#ff8585',
  borderColor: 'rgba(255, 255, 255, 0.18)',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 500,
  padding: '8px 14px',
  gap: '6px',
  '&:hover': {
    borderColor: '#FF6B6B',
    backgroundColor: 'rgba(255, 107, 107, 0.06)',
  },
};

/**
 * Renders the inspector for the selected branding hotspot.
 *
 * Every field autosaves through a PATCH and nudges UE with `SPOTS_UPDATED`. The
 * media card adapts to the hotspot variant: a `.glb` upload for 3D objects, an
 * image/video/URL picker for legacy 2D panels. 3D objects additionally expose
 * the world-interaction switch — off makes the object fully static — and a
 * Uniform ↔ Per-axis scale editor whose blur handlers PATCH `{ scale }` as
 * either a scalar or a `[x, y, z]` tuple.
 *
 * @param props.hotspotId - `brandingHotspotId` of the hotspot being edited.
 */
const HotspotInspector = ({ hotspotId }: HotspotInspectorProps) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const rootRef = usePreventGameKeydownListening<HTMLDivElement>();
  const { setAutosaveStatus, clearSelection } = useEditorMode();
  const {
    brandingHotspots,
    setBrandingHotspots,
    currentBrandingId,
    currentRoom,
    setPendingHotspotPlacement,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const { showNotification } = useNotification();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const hotspot = useMemo<AnyHotspot | null>(() => {
    const source = Array.isArray(brandingHotspots) ? brandingHotspots : [];
    return (
      (source as AnyHotspot[]).find(
        (h) => h.brandingHotspotId === hotspotId,
      ) ?? null
    );
  }, [brandingHotspots, hotspotId]);

  const is3D = String(hotspot?.type ?? '').toUpperCase() === 'OBJECT_3D';
  const fileUrl = hotspot?.data?.url || '';
  const externalUrl = hotspot?.data?.webBrowserUrl || '';
  const effectiveUrl = fileUrl || externalUrl;
  const storedKind: 'IMAGE' | 'VIDEO' = isVideoUrl(effectiveUrl)
    ? 'VIDEO'
    : 'IMAGE';

  const storedScale = hotspot?.scale as number | number[] | undefined;

  const [name, setName] = useState<string>(hotspot?.name ?? '');
  const [mediaKind, setMediaKind] = useState<'IMAGE' | 'VIDEO'>(storedKind);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Lazy initializers — `useState` reads the initializer once on mount, so
  // deriving inside a `useMemo` on every render was dead work.
  const [scaleMode, setScaleMode] = useState<ScaleMode>(
    () => deriveScaleState(storedScale).mode,
  );
  const [scaleUniformInput, setScaleUniformInput] = useState<string>(
    () => deriveScaleState(storedScale).uniform,
  );
  const [scaleXInput, setScaleXInput] = useState<string>(
    () => deriveScaleState(storedScale).x,
  );
  const [scaleYInput, setScaleYInput] = useState<string>(
    () => deriveScaleState(storedScale).y,
  );
  const [scaleZInput, setScaleZInput] = useState<string>(
    () => deriveScaleState(storedScale).z,
  );

  const activeMediaUrl =
    effectiveUrl && (is3D || storedKind === mediaKind) ? effectiveUrl : '';
  const visibleExternalUrl = !fileUrl && activeMediaUrl ? externalUrl : '';

  const [mediaUrlInput, setMediaUrlInput] =
    useState<string>(visibleExternalUrl);

  useEffect(() => {
    setName(hotspot?.name ?? '');
  }, [hotspotId, hotspot?.name]);

  useEffect(() => {
    setMediaKind(isVideoUrl(effectiveUrl) ? 'VIDEO' : 'IMAGE');
  }, [hotspotId, effectiveUrl]);

  useEffect(() => {
    setMediaUrlInput(visibleExternalUrl);
  }, [hotspotId, visibleExternalUrl]);

  useEffect(() => {
    if (!activeMediaUrl) {
      setLightboxOpen(false);
    }
  }, [activeMediaUrl]);

  /**
   * Resets the scale editor to reflect the currently selected hotspot.
   *
   * Only re-seeds when the *selected hotspot* changes — never on every
   * `storedScale` reference identity, which flips on any WS-driven
   * `setBrandingHotspots` refresh even when the underlying value is byte-for-byte
   * equal. A vec3 stored scale would arrive as a fresh `[x, y, z]` array on each
   * refresh and clobber the user's uncommitted keystrokes; scalars survived only
   * because `Object.is` treats them as equal. Matches the sibling
   * `useEffect([hotspotId, hotspot?.name])` pattern.
   */
  useEffect(() => {
    const next = deriveScaleState(storedScale);
    setScaleMode(next.mode);
    setScaleUniformInput(next.uniform);
    setScaleXInput(next.x);
    setScaleYInput(next.y);
    setScaleZInput(next.z);
    // storedScale is intentionally excluded — see JSDoc above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotspotId]);

  const refresh = useCallback(async () => {
    if (!currentBrandingId) {
      return;
    }
    const refreshed = await BrandingsService.getBrandingHotspots(
      currentBrandingId,
      { limit: 200 },
    );
    setBrandingHotspots(refreshed.hotspots ?? []);
  }, [currentBrandingId, setBrandingHotspots]);

  const persist = useCallback(
    async (
      patch: IUpdateBrandingHotspotData,
    ): Promise<IBrandingHotspotApi | null> => {
      if (!currentBrandingId || !hotspotId) {
        return null;
      }
      setAutosaveStatus('saving');
      try {
        const updated = await BrandingsService.patchBrandingHotspot(
          currentBrandingId,
          hotspotId,
          patch,
        );
        await refresh();
        setAutosaveStatus('saved');
        return updated;
      } catch (saveError) {
        console.error(
          `Failed to update hotspot: ${(saveError as Error)?.message}`,
          { brandingId: currentBrandingId, brandingHotspotId: hotspotId },
        );
        setAutosaveStatus('error');
        return null;
      } finally {
        sendMessageToUnreal({ action: 'SPOTS_UPDATED', spots: [] });
      }
    },
    [
      currentBrandingId,
      hotspotId,
      refresh,
      sendMessageToUnreal,
      setAutosaveStatus,
    ],
  );

  const handleNameBlur = useCallback(() => {
    if (name !== (hotspot?.name ?? '')) {
      persist({ name });
    }
  }, [name, hotspot?.name, persist]);

  const handleMediaUrlBlur = useCallback(() => {
    const trimmed = mediaUrlInput.trim();
    if (trimmed === visibleExternalUrl) {
      return;
    }
    persist({ data: { url: '', webBrowserUrl: trimmed } });
  }, [mediaUrlInput, visibleExternalUrl, persist]);

  const handleToggleActive = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      persist({ isActive: checked });
    },
    [persist],
  );

  const handleToggleInteractive = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      persist({ isInteractive: checked });
    },
    [persist],
  );

  /**
   * Handles blur on the uniform-scale input: reverts on invalid or unchanged
   * values, otherwise persists `{ scale: <number> }`. A stored uniform vec3
   * (`[n, n, n]`) is treated as equivalent to the scalar `n`, so re-entering
   * the same value never flips the shape from array to scalar via an
   * unnecessary PATCH + `SPOTS_UPDATED`.
   */
  const handleScaleUniformBlur = useCallback(() => {
    const parsed = parseScaleInput(scaleUniformInput);
    if (parsed === null) {
      // Invalid or empty input — revert to the stored value.
      const reverted = deriveScaleState(storedScale);
      setScaleUniformInput(reverted.uniform);
      return;
    }
    if (typeof storedScale === 'number' && storedScale === parsed) {
      // Same scalar as stored — no-op.
      setScaleUniformInput(formatScaleInput(parsed));
      return;
    }
    if (
      Array.isArray(storedScale) &&
      storedScale.length === 3 &&
      storedScale.every((component) => component === parsed)
    ) {
      // Stored as a uniform vec3 that already equals `[parsed, parsed, parsed]`
      // — don't PATCH just to collapse the shape to a scalar.
      setScaleUniformInput(formatScaleInput(parsed));
      return;
    }
    setScaleUniformInput(formatScaleInput(parsed));
    persist({ scale: parsed });
  }, [scaleUniformInput, storedScale, persist]);

  /**
   * Handles blur on a per-axis scale input. Reverts the specific axis on invalid
   * input, otherwise commits `{ scale: [x, y, z] }` when the triplet actually
   * differs from what is stored.
   *
   * Batch-commit semantics (deliberate): the PATCH always contains the *current
   * contents of all three inputs*, not only the axis that fired the blur. So
   * valid but uncommitted keystrokes on siblings ride along on the first
   * successful blur — one PATCH covers a multi-axis edit instead of three, and
   * a reverted axis re-enters the PATCH at its stored value. The alternative —
   * a per-axis "dirty" flag committing only the blurring axis — was rejected:
   * it fires N PATCHes for a user editing N axes and the axis-vs-axis timing
   * makes the intermediate DB states awkward.
   *
   * @param axis - Which axis input fired the blur.
   */
  const handleScaleAxisBlur = useCallback(
    (axis: 'x' | 'y' | 'z') => {
      const parsedX = parseScaleInput(scaleXInput);
      const parsedY = parseScaleInput(scaleYInput);
      const parsedZ = parseScaleInput(scaleZInput);

      const reverted = deriveScaleState(storedScale);
      if (axis === 'x' && parsedX === null) {
        setScaleXInput(reverted.x);
        return;
      }
      if (axis === 'y' && parsedY === null) {
        setScaleYInput(reverted.y);
        return;
      }
      if (axis === 'z' && parsedZ === null) {
        setScaleZInput(reverted.z);
        return;
      }
      if (parsedX === null || parsedY === null || parsedZ === null) {
        // Another axis is invalid — don't PATCH; that axis's own blur will handle it.
        return;
      }

      const nextArray: [number, number, number] = [parsedX, parsedY, parsedZ];

      if (
        Array.isArray(storedScale) &&
        storedScale.length === 3 &&
        storedScale[0] === nextArray[0] &&
        storedScale[1] === nextArray[1] &&
        storedScale[2] === nextArray[2]
      ) {
        return;
      }
      if (
        typeof storedScale === 'number' &&
        storedScale === nextArray[0] &&
        storedScale === nextArray[1] &&
        storedScale === nextArray[2]
      ) {
        return;
      }

      setScaleXInput(formatScaleInput(parsedX));
      setScaleYInput(formatScaleInput(parsedY));
      setScaleZInput(formatScaleInput(parsedZ));
      persist({ scale: nextArray });
    },
    [scaleXInput, scaleYInput, scaleZInput, storedScale, persist],
  );

  /**
   * Handles the Uniform ↔ Per-axis toggle. Switching to Per-axis pre-fills all
   * three axes with the current uniform value; switching to Uniform seeds the
   * single field from the X component (or the uniform value when all axes are
   * equal). The toggle itself never persists — only field blurs do.
   *
   * @param _event - Ignored change event.
   * @param checked - `true` selects Per-axis mode.
   */
  const handleScaleModeToggle = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (checked) {
        // Uniform → Per-axis: pre-fill X/Y/Z from the current uniform value so
        // the user doesn't lose it (spec forbids clobbering to 1/1/1).
        setScaleXInput(scaleUniformInput);
        setScaleYInput(scaleUniformInput);
        setScaleZInput(scaleUniformInput);
        setScaleMode('PER_AXIS');
        return;
      }
      // Per-axis → Uniform: use X as the seed regardless of whether the axes
      // agree; equal axes make X === Y === Z so this is loss-free anyway.
      setScaleUniformInput(scaleXInput);
      setScaleMode('UNIFORM');
    },
    [scaleUniformInput, scaleXInput],
  );

  const validateUploadFile = useCallback(
    (file: File): string | null => {
      const lowerName = file.name.toLowerCase();
      if (is3D) {
        if (MODEL_FILENAME_RE.test(lowerName) || file.type === MODEL_MIME_TYPE) {
          return null;
        }
        return translate(
          'editorMode.hotspotUnsupportedModel',
          'Unsupported format. Please upload a .glb 3D model file.',
        );
      }
      if (mediaKind === 'VIDEO') {
        if (
          ALLOWED_VIDEO_MIME_TYPES.includes(file.type) ||
          VIDEO_FILENAME_RE.test(lowerName)
        ) {
          return null;
        }
        return translate(
          'editorMode.hotspotUnsupportedVideo',
          'Unsupported format. Please upload an MP4, WebM, or MOV video file.',
        );
      }
      if (
        ALLOWED_IMAGE_MIME_TYPES.includes(file.type) ||
        IMAGE_FILENAME_RE.test(lowerName)
      ) {
        return null;
      }
      return translate(
        'editorMode.hotspotUnsupportedImage',
        'Unsupported format. Please upload a PNG or JPG image file.',
      );
    },
    [is3D, mediaKind, translate],
  );

  const isPlacedInWorld =
    Array.isArray(hotspot?.position) && (hotspot?.position?.length ?? 0) > 0;

  const handleUploadFiles = useCallback(
    async (files: FileList | null) => {
      const file = files?.[0];
      if (!file || !currentBrandingId) {
        return;
      }
      const validationError = validateUploadFile(file);
      if (validationError) {
        showNotification({
          message: validationError,
          type: NOTIFICATION_TYPES.ERROR,
        });
        return;
      }
      setIsUploading(true);
      setUploadProgress(0);
      setAutosaveStatus('saving');
      try {
        let fileMd5: string | undefined;
        if (is3D) {
          fileMd5 = md5(await file.arrayBuffer());
        }
        const [uploaded] = await FilesService.uploadFilesWithProgress(
          [file],
          (progress: number) => setUploadProgress(progress),
        );
        if (uploaded?.url) {
          const updated = await persist({
            data: {
              url: uploaded.url,
              location: uploaded.location,
              ...(fileMd5 ? { md5: fileMd5 } : {}),
              webBrowserUrl: '',
            },
          });
          const relocatedUrl = updated?.data?.url;
          if (is3D && !isPlacedInWorld && relocatedUrl) {
            const relocatedMd5 = updated?.data?.md5 ?? fileMd5;
            setPendingHotspotPlacement({
              brandingHotspotId: hotspotId,
              isCreated: true,
            });
            startHotspotPlaceInUnreal(sendMessageToUnreal, '3D', {
              brandingHotspotId: hotspotId,
              ...(name || hotspot?.name
                ? { name: name || hotspot?.name }
                : {}),
              ...(currentRoom?.roomId ? { roomId: currentRoom.roomId } : {}),
              data: {
                url: relocatedUrl,
                ...(relocatedMd5 ? { md5: relocatedMd5 } : {}),
              },
            });
            showNotification({
              message: translate(
                'editorMode.hotspotPlacementStarted',
                'Model uploaded — now place the object in the world.',
              ),
              type: NOTIFICATION_TYPES.SUCCESS,
            });
          }
        }
      } catch (uploadError) {
        console.error(
          `Failed to upload hotspot media: ${(uploadError as Error)?.message}`,
          { brandingId: currentBrandingId, brandingHotspotId: hotspotId },
        );
        setAutosaveStatus('error');
      } finally {
        setIsUploading(false);
      }
    },
    [
      currentBrandingId,
      currentRoom,
      hotspot,
      hotspotId,
      is3D,
      isPlacedInWorld,
      name,
      persist,
      sendMessageToUnreal,
      setAutosaveStatus,
      setPendingHotspotPlacement,
      showNotification,
      translate,
      validateUploadFile,
    ],
  );

  const handleUploadClick = useCallback(() => {
    const input = fileInputRef.current;
    if (!input) {
      return;
    }
    input.value = '';
    input.click();
  }, []);

  const handleDeleteMedia = useCallback(() => {
    persist({ data: { url: '', webBrowserUrl: '' } });
  }, [persist]);

  const handleSaveHotspot = useCallback(() => {
    persist({
      name,
      isActive: hotspot?.isActive !== false,
      ...(is3D ? { isInteractive: hotspot?.isInteractive !== false } : {}),
    });
  }, [persist, name, hotspot?.isActive, hotspot?.isInteractive, is3D]);

  const handleDelete = useCallback(async () => {
    if (!currentBrandingId || !hotspotId) {
      return;
    }
    setAutosaveStatus('saving');
    try {
      await BrandingsService.deleteBrandingHotspot(currentBrandingId, hotspotId);
      await refresh();
      sendMessageToUnreal({ action: 'SPOTS_UPDATED', spots: [] });
      setAutosaveStatus('saved');
      clearSelection();
    } catch (deleteError) {
      console.error(
        `Failed to delete hotspot: ${(deleteError as Error)?.message}`,
        { brandingId: currentBrandingId, brandingHotspotId: hotspotId },
      );
      setAutosaveStatus('error');
    }
  }, [
    currentBrandingId,
    hotspotId,
    refresh,
    sendMessageToUnreal,
    setAutosaveStatus,
    clearSelection,
  ]);

  const mediaFilename = getFilename(activeMediaUrl);
  const showImageThumb = !is3D && !!activeMediaUrl && mediaKind === 'IMAGE';
  const showVideoThumb = !is3D && !!activeMediaUrl && mediaKind === 'VIDEO';
  const canZoomMedia = !is3D && !!activeMediaUrl;

  return (
    <Box ref={rootRef}>
      <Box sx={styles.section}>
        <Typography sx={styles.fieldLabel}>
          {translate('editorMode.hotspotName', 'Name')}
        </Typography>
        <TextField
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleNameBlur}
          size="small"
          fullWidth
          sx={styles.textField}
        />
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.fieldLabel}>
          {is3D
            ? translate('editorMode.hotspotModel', '3D model')
            : mediaKind === 'VIDEO'
            ? translate('editorMode.video', 'Video')
            : translate('editorMode.hotspotImage', 'Image')}
        </Typography>
        {!is3D && (
          <Box sx={[styles.segmented, { marginBottom: '8px' }]}>
            <Box
              sx={[
                styles.segmentedItem,
                ...(mediaKind === 'IMAGE' ? [styles.segmentedItemActive] : []),
              ]}
              onClick={() => setMediaKind('IMAGE')}
            >
              {translate('editorMode.image', 'Image')}
            </Box>
            <Box
              sx={[
                styles.segmentedItem,
                ...(mediaKind === 'VIDEO' ? [styles.segmentedItemActive] : []),
              ]}
              onClick={() => setMediaKind('VIDEO')}
            >
              {translate('editorMode.video', 'Video')}
            </Box>
          </Box>
        )}
        {is3D && !effectiveUrl && (
          <Box sx={uploadModelBannerStyles}>
            <InfoOutlinedIcon
              sx={{ fontSize: 16, flexShrink: 0, marginTop: '1px' }}
            />
            <Typography sx={{ fontSize: '12px', lineHeight: 1.45 }}>
              {translate(
                'editorMode.hotspotUploadModelFirst',
                "Upload a 3D model (.glb) first — after the upload you'll place the object in the world.",
              )}
            </Typography>
          </Box>
        )}
        <Box sx={styles.mediaCard}>
          <Box sx={styles.compactRow}>
            <Box
              sx={[
                styles.compactThumb,
                ...(!showImageThumb && !showVideoThumb
                  ? [styles.spotThumbPlaceholder]
                  : []),
                ...(canZoomMedia ? [{ cursor: 'zoom-in' }] : []),
              ]}
              style={
                showImageThumb
                  ? { backgroundImage: `url("${activeMediaUrl}")` }
                  : undefined
              }
              onClick={canZoomMedia ? () => setLightboxOpen(true) : undefined}
            >
              {showVideoThumb && (
                <Box
                  component="video"
                  src={`${activeMediaUrl}#t=0.1`}
                  muted
                  playsInline
                  preload="metadata"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    pointerEvents: 'none',
                  }}
                />
              )}
              {!showImageThumb &&
                !showVideoThumb &&
                (is3D ? (
                  <ViewInArOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
                ) : mediaKind === 'VIDEO' ? (
                  <PlayArrowOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
                ) : (
                  <ImageOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
                ))}
              {canZoomMedia && (
                <Box sx={styles.thumbExpandBadge}>
                  <OpenInFullOutlinedIcon sx={{ fontSize: 10 }} />
                </Box>
              )}
            </Box>

            <Box sx={styles.compactInfo}>
              <Typography sx={styles.compactTitle}>
                {mediaFilename ||
                  translate('editorMode.noMedia', 'No media set yet')}
              </Typography>
              <Typography sx={styles.compactSubtitle}>
                {is3D
                  ? translate('editorMode.hotspotModelKind', '3D model (.glb)')
                  : mediaKind === 'VIDEO'
                  ? translate('editorMode.video', 'Video')
                  : translate('editorMode.image', 'Image')}
              </Typography>
            </Box>

            <Box sx={styles.compactActions}>
              <IconButton
                size="small"
                sx={styles.iconSquareButton}
                onClick={handleUploadClick}
                disabled={isUploading}
                aria-label={translate('editorMode.uploadMedia', 'Upload')}
              >
                <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
              {!!activeMediaUrl && (
                <IconButton
                  size="small"
                  sx={[styles.iconSquareButton, styles.iconDangerButton]}
                  onClick={handleDeleteMedia}
                  disabled={isUploading}
                  aria-label={translate('editorMode.delete', 'Delete')}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          </Box>

          {isUploading && (
            <Box sx={styles.progressBlock}>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={styles.compactProgressBar}
              />
            </Box>
          )}

          <Box sx={styles.compactDivider} />

          <Box>
            <Typography sx={styles.fieldLabel}>
              {translate('editorMode.hotspotMediaUrl', 'Or paste a direct URL')}
            </Typography>
            <TextField
              value={mediaUrlInput}
              onChange={(e) => setMediaUrlInput(e.target.value)}
              onBlur={handleMediaUrlBlur}
              size="small"
              fullWidth
              placeholder="https://…"
              sx={styles.textField}
            />
          </Box>
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept={
            is3D
              ? '.glb,model/gltf-binary'
              : mediaKind === 'VIDEO'
              ? 'video/mp4,video/webm,video/quicktime'
              : 'image/png,image/jpeg'
          }
          style={{ display: 'none' }}
          onChange={(e) => handleUploadFiles(e.target.files)}
        />
      </Box>

      <Box sx={styles.section}>
        <Box sx={activeRowStyles}>
          <Typography
            sx={{ fontSize: '13px', fontWeight: 500, color: COLORS.WHITE }}
          >
            {translate('editorMode.hotspotScale', 'Scale')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Typography
              sx={{
                fontSize: '12px',
                color:
                  scaleMode === 'UNIFORM' ? COLORS.WHITE : 'rgba(255,255,255,0.5)',
              }}
            >
              {translate('editorMode.hotspotScaleUniform', 'Uniform')}
            </Typography>
            <Switch
              size="small"
              checked={scaleMode === 'PER_AXIS'}
              onChange={handleScaleModeToggle}
              inputProps={{
                'aria-label': translate(
                  'editorMode.hotspotScale',
                  'Scale',
                ) as string,
                // React DOM forwards data-* on the underlying <input>.
                ...({
                  'data-testid': 'hotspot-inspector-scale-mode-switch',
                } as Record<string, string>),
              }}
            />
            <Typography
              sx={{
                fontSize: '12px',
                color:
                  scaleMode === 'PER_AXIS' ? COLORS.WHITE : 'rgba(255,255,255,0.5)',
              }}
            >
              {translate('editorMode.hotspotScalePerAxis', 'Per axis')}
            </Typography>
          </Box>
        </Box>
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.5)',
            fontSize: '11px',
            padding: '0 4px',
            lineHeight: 1.4,
          }}
        >
          {translate(
            'editorMode.hotspotScaleHint',
            'Uniform — one factor applied to all axes. Per axis — set X, Y and Z separately.',
          )}
        </Typography>
        {scaleMode === 'UNIFORM' ? (
          <TextField
            value={scaleUniformInput}
            onChange={(e) => setScaleUniformInput(e.target.value)}
            onBlur={handleScaleUniformBlur}
            size="small"
            fullWidth
            type="number"
            inputProps={{
              min: SCALE_MIN,
              max: SCALE_MAX,
              step: 0.01,
              'data-testid': 'hotspot-inspector-scale-uniform-input',
            }}
            sx={styles.textField}
          />
        ) : (
          <Box sx={{ display: 'flex', gap: '6px' }}>
            <TextField
              value={scaleXInput}
              onChange={(e) => setScaleXInput(e.target.value)}
              onBlur={() => handleScaleAxisBlur('x')}
              size="small"
              fullWidth
              type="number"
              label={translate('editorMode.hotspotScaleX', 'X')}
              inputProps={{
                min: SCALE_MIN,
                max: SCALE_MAX,
                step: 0.01,
                'data-testid': 'hotspot-inspector-scale-x-input',
              }}
              sx={styles.textField}
            />
            <TextField
              value={scaleYInput}
              onChange={(e) => setScaleYInput(e.target.value)}
              onBlur={() => handleScaleAxisBlur('y')}
              size="small"
              fullWidth
              type="number"
              label={translate('editorMode.hotspotScaleY', 'Y')}
              inputProps={{
                min: SCALE_MIN,
                max: SCALE_MAX,
                step: 0.01,
                'data-testid': 'hotspot-inspector-scale-y-input',
              }}
              sx={styles.textField}
            />
            <TextField
              value={scaleZInput}
              onChange={(e) => setScaleZInput(e.target.value)}
              onBlur={() => handleScaleAxisBlur('z')}
              size="small"
              fullWidth
              type="number"
              label={translate('editorMode.hotspotScaleZ', 'Z')}
              inputProps={{
                min: SCALE_MIN,
                max: SCALE_MAX,
                step: 0.01,
                'data-testid': 'hotspot-inspector-scale-z-input',
              }}
              sx={styles.textField}
            />
          </Box>
        )}
      </Box>

      <Box sx={styles.section}>
        <Box sx={activeRowStyles}>
          <Typography
            sx={{ fontSize: '13px', fontWeight: 500, color: COLORS.WHITE }}
          >
            {translate('editorMode.hotspotActive', 'Active')}
          </Typography>
          <Switch
            size="small"
            checked={hotspot?.isActive !== false}
            onChange={handleToggleActive}
            data-testid="hotspot-inspector-active-switch"
          />
        </Box>
      </Box>

      {is3D && (
        <Box sx={styles.section}>
          <Box sx={activeRowStyles}>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 500, color: COLORS.WHITE }}
            >
              {translate(
                'editorMode.hotspotInteractive',
                'Interactive in world',
              )}
            </Typography>
            <Switch
              size="small"
              checked={hotspot?.isInteractive !== false}
              onChange={handleToggleInteractive}
              inputProps={{
                'aria-label': translate(
                  'editorMode.hotspotInteractive',
                  'Interactive in world',
                ),
              }}
              data-testid="hotspot-inspector-interactive-switch"
            />
          </Box>
          <Typography sx={toggleHintStyles}>
            {translate(
              'editorMode.hotspotInteractiveHint',
              'Off — the object becomes fully static: it cannot be clicked and is not highlighted on hover.',
            )}
          </Typography>
        </Box>
      )}

      <Box sx={styles.section}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={handleSaveHotspot}
          sx={saveButtonStyles}
        >
          {translate('editorMode.saveHotspot', 'Save hotspot')}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<DeleteOutlineIcon />}
          onClick={handleDelete}
          sx={deleteButtonStyles}
        >
          {translate('editorMode.deleteHotspot', 'Delete hotspot')}
        </Button>
      </Box>

      <Modal
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        sx={styles.lightboxBackdrop}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none',
          }}
          onClick={() => setLightboxOpen(false)}
        >
          {activeMediaUrl && mediaKind === 'VIDEO' ? (
            <Box
              component="video"
              src={activeMediaUrl}
              controls
              autoPlay
              sx={styles.lightboxImg}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
          ) : activeMediaUrl ? (
            <Box
              component="img"
              src={activeMediaUrl}
              alt={mediaFilename}
              sx={styles.lightboxImg}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
          ) : null}
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={styles.lightboxClose}
            size="small"
            aria-label={translate('editorMode.close')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Modal>
    </Box>
  );
};

export default HotspotInspector;
